/**
 * PD-15865 — vocabulary the Matching queue's filters share between the filter
 * tray (libs/core), the list endpoint (crm-api) and the review screens.
 */

/**
 * Confidence bands for the Matching queue's Confidence filter (Chris
 * 2026-09-03: a band, not a minimum). `min` is inclusive and `max` exclusive,
 * so the bands never overlap on a fractional score such as 94.5; null means
 * unbounded.
 *
 * The ids are deliberately not numeric: the list's URL state turns any id that
 * `parseInt` can read into a number (ListStateStorageService.parseUrlParams),
 * which would break a shared link.
 */
export const MATCH_CONFIDENCE_BANDS = [
	{ id: 'band_95_100', label: '95–100%', min: 95, max: null },
	{ id: 'band_90_94', label: '90–94%', min: 90, max: 95 },
	{ id: 'band_80_89', label: '80–89%', min: 80, max: 90 },
	{ id: 'band_70_79', label: '70–79%', min: 70, max: 80 },
	{ id: 'band_under_70', label: 'Under 70%', min: null, max: 70 },
] as const satisfies ReadonlyArray<{
	id: string;
	label: string;
	min: number | null;
	max: number | null;
}>;

export type MatchConfidenceBandId =
	(typeof MATCH_CONFIDENCE_BANDS)[number]['id'];

/** The band with this id, or undefined for an id that is not a band. */
export function findMatchConfidenceBand(
	id: string,
): (typeof MATCH_CONFIDENCE_BANDS)[number] | undefined {
	return MATCH_CONFIDENCE_BANDS.find((band) => band.id === id);
}

/**
 * The matchmaker passes that leave a PENDING suggestion
 * (`client_license_match.match_method` on a pending_review row), in pass order.
 * `link_modal` and `manual` are absent: a human's match is confirmed on the
 * spot and never queues. Pharmsource prod on 2026-09-26 held five of these:
 * name_address_fuzzy 46,678, license_number 12,233, dea_number 7,061,
 * replacement_candidate 170, fl_drug_permit_number_hint 44.
 * `relocation_successor` (PD-16585) is the license a board recorded as
 * replacing a Closed one the customer matched.
 */
export const PENDING_MATCH_METHODS = [
	'npi_direct',
	'license_number',
	'fl_drug_permit_number_hint',
	'npi_address_crosswalk',
	'name_address_fuzzy',
	'dea_number',
	'name_only',
	'replacement_candidate',
	'relocation_successor',
] as const;

export type PendingMatchMethod = (typeof PENDING_MATCH_METHODS)[number];

/**
 * Human-readable label for the matchmaker's raw `match_method` key — the stored
 * value (e.g. "license_number") is a machine token a reviewer can't interpret.
 * Phrased as the REASON the match was surfaced. Unknown keys fall back to a
 * title-cased "Matched on <key>" so a new method never shows a raw token.
 *
 * Shared by the Pending Matches wizard, the license detail Overview's Registry
 * Data panel and the Matching queue's Source filter so all three read the same
 * way.
 */
const MATCH_METHOD_LABELS: Readonly<Record<string, string>> = {
	license_number: 'Matched on License #',
	dea_number: 'Matched on DEA #',
	npi_direct: 'Matched on NPI',
	npi_address_crosswalk: 'Matched on NPI + address',
	name_address_fuzzy: 'Matched on name + address',
	name_only: 'Matched on name',
	fl_drug_permit_number_hint: 'Matched on FL drug permit #',
	relocation_successor: 'Board-recorded successor',
	link_modal: 'Linked manually',
	manual: 'Matched manually',
};

export function matchMethodLabel(method: string | null | undefined): string {
	if (method == null || method === '') {
		return 'Matched';
	}
	const known = MATCH_METHOD_LABELS[method];
	if (known != null) {
		return known;
	}
	const humanized = method
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (c) => c.toUpperCase());
	return `Matched on ${humanized}`;
}
